/**
 * Hook for ERC20 token approvals
 * Required before depositing assets to CollateralVault
 */

import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { ERC20_ABI } from '@contracts/abis'
import { Address } from '../types/contracts'
import { toast } from 'react-hot-toast'

/**
 * Hook to check token allowance
 */
export function useTokenAllowance(
  tokenAddress?: Address,
  owner?: Address,
  spender?: Address
) {
  const { data: allowance, isLoading, refetch } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: owner && spender ? [owner, spender] : undefined,
    query: {
      enabled: !!tokenAddress && !!owner && !!spender,
    },
  })

  return {
    allowance: allowance as bigint | undefined,
    isLoading,
    refetch,
  }
}

/**
 * Hook to check token balance
 */
export function useTokenBalance(tokenAddress?: Address, account?: Address) {
  const { data: balance, isLoading, refetch } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: account ? [account] : undefined,
    query: {
      enabled: !!tokenAddress && !!account,
    },
  })

  return {
    balance: balance as bigint | undefined,
    isLoading,
    refetch,
  }
}

/**
 * Hook to approve token spending
 */
export function useApproveToken() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const approve = async (tokenAddress: Address, spender: Address, amount: bigint) => {
    try {
      console.log('🟢 [useApproveToken] Approving token', {
        token: tokenAddress,
        spender,
        amount: amount.toString()
      })

      writeContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [spender, amount],
        gas: 60000n, // Standard gas for ERC20 approval
      })
    } catch (err) {
      toast.error('Failed to approve token')
      console.error('🔴 [useApproveToken] Error:', err)
    }
  }

  return {
    approve,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to get token info (name, symbol, decimals)
 */
export function useTokenInfo(tokenAddress?: Address) {
  const { data: name } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'name',
    query: {
      enabled: !!tokenAddress,
    },
  })

  const { data: symbol } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'symbol',
    query: {
      enabled: !!tokenAddress,
    },
  })

  const { data: decimals } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'decimals',
    query: {
      enabled: !!tokenAddress,
    },
  })

  return {
    name: name as string | undefined,
    symbol: symbol as string | undefined,
    decimals: decimals as number | undefined,
  }
}

